// TodayMacroBanner — kompakte „Heute · Macro"-Übersicht für Markt-Page
// und Right-Rail. Holt live Daten von /trading/macro/today (Finnhub) und
// listet Top-3-5 Events. Klick auf Card-Body öffnet /trading/macro für
// Detail + EVE-Forecast.
//
// Doc-Anweisung 2026-05-17:
//   1) im Hauptdashboard prominent („heute stehen xy an — achtung — link")
//   2) HEUTE-Macro im Right-Rail aus Demo-Daten auf echte Daten umstellen

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, Sparkles } from 'lucide-react';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { fetchTodayReleases, type MacroRelease } from '@/lib/macro-api';
import { formatMacroTime } from '@/lib/macro-time';

type Variant = 'banner' | 'rail';

interface TodayMacroBannerProps {
  /** banner = volle Markt-Page-Anzeige · rail = kompakt für Right-Rail Daten-Tab. */
  variant?: Variant;
  /** Filter auf High-Impact-Events (Doc-Anweisung „achtung"). Default true. */
  highOnly?: boolean;
  /** Max Anzahl Events anzeigen. Default 5. */
  limit?: number;
  /** Wenn nach Filter 0 Events übrig sind, gar nichts rendern (null). Default false. */
  hideWhenEmpty?: boolean;
}

const IMPACT_TONE: Record<MacroRelease['importance'], string> = {
  high: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
  medium: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  low: 'border-white/10 bg-white/[0.03] text-neutral-400',
};

function countryFlag(country: string): string {
  switch (country.toUpperCase()) {
    case 'US':
      return '🇺🇸';
    case 'DE':
      return '🇩🇪';
    case 'EU':
      return '🇪🇺';
    case 'GB':
      return '🇬🇧';
    default:
      return '🌐';
  }
}

export function TodayMacroBanner({
  variant = 'banner',
  highOnly = true,
  limit = 5,
  hideWhenEmpty = false,
}: TodayMacroBannerProps) {
  const accessToken = useAuthStore(selectAccessToken);
  const [releases, setReleases] = useState<MacroRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    fetchTodayReleases(accessToken)
      .then((res) => {
        if (cancelled) return;
        setReleases(res.releases ?? []);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const filtered = (highOnly ? releases.filter((r) => r.importance === 'high') : releases).slice(
    0,
    limit,
  );

  if (hideWhenEmpty && !loading && !error && filtered.length === 0) {
    return null;
  }

  const containerClass =
    variant === 'banner'
      ? 'glass-strong rounded-2xl border border-amber-500/20 p-4'
      : 'glass rounded-xl p-3';

  const headerCount = filtered.length;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <section className={containerClass}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {variant === 'banner' ? (
            <AlertCircle className="size-4 text-amber-400" />
          ) : null}
          <h3
            className={
              variant === 'banner'
                ? 'text-[13px] font-bold uppercase tracking-wider text-amber-300'
                : 'text-[12px] uppercase tracking-wider text-neutral-500'
            }
          >
            {variant === 'banner' ? 'Heute · Wichtige Releases' : 'Heute · Macro'}
          </h3>
          {headerCount > 0 ? (
            <span className="rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300">
              {headerCount}
            </span>
          ) : null}
        </div>
        <Link
          to="/trading/macro"
          className="inline-flex items-center gap-1 text-[11px] text-amber-300/80 hover:text-amber-200 hover:underline"
        >
          Alle ansehen
          <ChevronRight className="size-3" />
        </Link>
      </div>

      {error ? (
        <div className="text-[11px] text-rose-300">{error}</div>
      ) : loading ? (
        <div className="py-3 text-center text-[11px] text-neutral-500">Lade Kalender…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-center text-[11px] text-neutral-500">
          {highOnly
            ? 'Heute keine High-Impact-Releases.'
            : 'Heute keine Wirtschafts-Releases.'}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {filtered.map((r, i) => {
            const t = formatMacroTime(r.release_time_et, today);
            return (
              <li
                key={`${r.indicator}-${r.release_time_et}-${i}`}
                className="flex items-center gap-2 text-[13px]"
              >
                <span className="shrink-0 text-base" title={r.country}>
                  {countryFlag(r.country)}
                </span>
                <span className="w-20 shrink-0 tabular text-amber-400">
                  {t.berlin}
                </span>
                <span className="flex-1 truncate text-neutral-200" title={r.indicator}>
                  {r.indicator}
                </span>
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase ${IMPACT_TONE[r.importance]}`}
                >
                  {r.importance === 'high'
                    ? 'HOCH'
                    : r.importance === 'medium'
                      ? 'MED'
                      : 'NIEDR'}
                </span>
                <span className="hidden text-[10px] tabular text-neutral-500 md:inline">
                  {t.et} ET
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {variant === 'banner' && filtered.length > 0 ? (
        <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-2 text-[11px] text-neutral-500">
          <span>Klick auf „Alle ansehen" für EVE-Prognose pro Event.</span>
          <Link
            to="/trading/macro"
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 font-bold text-amber-300 hover:bg-amber-500/25"
          >
            <Sparkles className="size-3" />
            EVE-Macro-Tab
          </Link>
        </div>
      ) : null}
    </section>
  );
}
